/*
 * Noxrivet cart drawer (snippets/cart-drawer.liquid, assets/nxd.css).
 *
 * Keeps Dawn's <cart-drawer> contract used by product-form.js, quick-add.js, quick-order-list.js,
 * quick-add-bulk.js and nx-home.js: open(), close(), renderContents(), getSectionsToRender(),
 * getSectionInnerHTML(), setActiveElement().
 *
 * Line changes go to /cart/change by line-item key, one request at a time. Clicks made while a
 * request is in flight only update the wanted quantity, so the newest choice is always sent last and
 * an older response can never overwrite it. The drawer is re-rendered once, from the section HTML
 * Shopify returns with the final response, so every count, price and total comes from Shopify.
 */
if (!customElements.get('cart-drawer')) {
  const strings = () => window.nxdStrings || {};
  const fill = (text, values) =>
    Object.entries(values).reduce((out, [k, v]) => out.split(`[${k}]`).join(v), text || '');
  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');
  const isShown = (el) =>
    !!(el && el.isConnected && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const jsonPost = (body) => {
    const config =
      typeof fetchConfig === 'function'
        ? fetchConfig()
        : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
    return { ...config, body: JSON.stringify(body) };
  };
  // Other scripts that render the drawer themselves after changing the cart.
  const SELF_RENDERING_SOURCES = ['cart-drawer', 'product-form', 'nx-look'];

  class CartDrawer extends HTMLElement {
    constructor() {
      super();
      this.pending = new Map(); // line key -> { quantity, title } not yet sent
      this.busy = false;
      this.errors = new Map(); // line key (or '_cart') -> message to show after the next render
      this.waiters = [];
      this.renderedAt = Date.now();
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusin = this.onFocusin.bind(this);
      this.onViewport = () => {
        cancelAnimationFrame(this.viewportFrame);
        this.viewportFrame = requestAnimationFrame(() => this.updateStackMode());
      };

      this.addEventListener('click', (event) => {
        const step = event.target.closest('[data-nxd-step]');
        if (step) {
          event.preventDefault();
          this.onStep(step);
          return;
        }
        const remove = event.target.closest('[data-nxd-remove]');
        if (remove) {
          event.preventDefault();
          this.onRemove(remove);
          return;
        }
        if (event.target.closest('#CartDrawer-Overlay, [data-nxd-close]')) {
          event.preventDefault();
          this.close();
        }
      });
      this.addEventListener('change', (event) => {
        if (event.target.matches('.nxd-qty__input')) this.onQuantityInput(event.target);
        else if (event.target.matches('[data-nxd-note]')) this.saveNote(event.target);
      });
      this.addEventListener('input', (event) => {
        if (!event.target.matches('[data-nxd-note]')) return;
        clearTimeout(this.noteTimer);
        this.noteTimer = setTimeout(() => this.saveNote(event.target), 500);
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('.nxd-qty__input')) {
          event.preventDefault();
          this.onQuantityInput(event.target);
        }
      });
      this.addEventListener('submit', (event) => this.onSubmit(event));
      // Native <dialog>: Escape fires "cancel" — run the animated close instead of the instant one.
      this.addEventListener(
        'cancel',
        (event) => {
          event.preventDefault();
          this.close();
        },
        true
      );
      this.addEventListener('close', () => this.classList.contains('active') && this.close(), true);
    }

    connectedCallback() {
      this.bindHeaderIcon();
      if (!this.unsubscribe && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (!SELF_RENDERING_SOURCES.includes(event && event.source)) this.refresh();
        });
      }
      // Warm the display font so the heading never flashes in a fallback face on first open.
      const warm = () => {
        const family = getComputedStyle(this).getPropertyValue('--nxd-display');
        if (family && document.fonts) document.fonts.load(`400 60px ${family}`).catch(() => {});
      };
      if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
      else setTimeout(warm, 2000);
      if (!this.pageshowBound) {
        this.pageshowBound = true;
        // Back/forward cache: the cart may have changed on another page or tab.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted) {
            this.renderedAt = 0;
            this.refresh();
          }
        });
      }
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
    }

    get dialog() {
      return this.querySelector('#CartDrawer');
    }

    get panel() {
      return this.querySelector('.drawer__inner');
    }

    get body() {
      return this.querySelector('.nxd__body');
    }

    /* ---------- header cart icon ---------- */

    bindHeaderIcon() {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        icon.setAttribute('role', 'button');
        icon.setAttribute('aria-haspopup', 'dialog');
        icon.setAttribute('aria-controls', 'CartDrawer');
        icon.setAttribute('aria-expanded', String(this.classList.contains('active')));
      }
      if (window.nxdCartIconBound) return;
      window.nxdCartIconBound = true;
      // Delegated once, so a re-rendered header keeps working and listeners never stack up.
      document.addEventListener('click', (event) => {
        const link = event.target.closest && event.target.closest('#cart-icon-bubble');
        const drawer = document.querySelector('cart-drawer');
        if (!link || !drawer || event.defaultPrevented) return;
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button) return;
        event.preventDefault();
        drawer.open(link);
      });
      document.addEventListener('keydown', (event) => {
        if (event.code !== 'Space' || !event.target.closest || !event.target.closest('#cart-icon-bubble')) return;
        const drawer = document.querySelector('cart-drawer');
        if (!drawer) return;
        event.preventDefault();
        drawer.open(event.target.closest('#cart-icon-bubble'));
      });
    }

    setIconExpanded(open) {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) icon.setAttribute('aria-expanded', String(open));
    }

    /* ---------- open / close ---------- */

    setActiveElement(element) {
      this.activeElement = element;
    }

    open(triggeredBy) {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const panel = this.panel;
      if (!panel) return;
      if (this.classList.contains('active')) {
        this.updateStackMode();
        this.focusPanel();
        return;
      }
      if (!this.activeElement && document.activeElement && !this.contains(document.activeElement)) {
        this.setActiveElement(document.activeElement);
      }
      clearTimeout(this.closeTimer);
      const dialog = this.dialog;
      if (dialog && !dialog.open) {
        try {
          dialog.showModal(); // top layer: above every page layer and app widget
        } catch (error) {
          dialog.setAttribute('open', '');
        }
      }
      this.lockPage();
      void panel.offsetWidth; // start the slide from the closed position, also after a fresh render
      this.classList.add('animate', 'active');
      this.setIconExpanded(true);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('focusin', this.onFocusin);
      window.addEventListener('resize', this.onViewport);
      if (window.visualViewport) window.visualViewport.addEventListener('resize', this.onViewport);
      this.updateStackMode();
      requestAnimationFrame(() => this.focusPanel());
      if (Date.now() - this.renderedAt > 1500) this.refresh();
    }

    close() {
      if (!this.classList.contains('active')) return;
      this.classList.remove('active', 'animate');
      this.setIconExpanded(false);
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('focusin', this.onFocusin);
      window.removeEventListener('resize', this.onViewport);
      if (window.visualViewport) window.visualViewport.removeEventListener('resize', this.onViewport);
      const dialog = this.dialog;
      // The modal keeps the page inert until it really closes, so focus goes back after that.
      const finish = () => {
        if (this.classList.contains('active')) return;
        if (dialog && dialog.open) dialog.close();
        const back = isShown(this.activeElement) ? this.activeElement : document.getElementById('cart-icon-bubble');
        if (back) back.focus({ preventScroll: true });
      };
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      clearTimeout(this.closeTimer);
      this.unlockPage();
      if (reduce || !dialog || !dialog.open) finish();
      else this.closeTimer = setTimeout(finish, 340); // after the slide-out
    }

    focusPanel() {
      const dialog = this.dialog;
      const panel = this.panel;
      if (dialog && this.classList.contains('active') && !(panel && panel.contains(document.activeElement))) {
        dialog.focus({ preventScroll: true });
      }
    }

    lockPage() {
      const root = document.documentElement;
      root.style.setProperty('--nxd-scrollbar', `${Math.max(0, window.innerWidth - root.clientWidth)}px`);
      root.classList.add('nxd-locked');
      // Everything outside the dialog is inert while it is open (focus and screen readers stay inside).
      this.inerted = [...document.body.children].filter(
        (el) => !el.contains(this) && !el.inert && !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(el.tagName)
      );
      this.inerted.forEach((el) => {
        el.inert = true;
      });
    }

    unlockPage() {
      (this.inerted || []).forEach((el) => {
        el.inert = false;
      });
      this.inerted = [];
      document.documentElement.classList.remove('nxd-locked');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const panel = this.panel;
      const dialog = this.dialog;
      const items = [...panel.querySelectorAll(FOCUSABLE)].filter(isShown);
      const active = document.activeElement;
      if (!items.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (active === first || !panel.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !panel.contains(active))) {
        event.preventDefault();
        first.focus();
      }
    }

    onFocusin(event) {
      if (this.classList.contains('active') && !this.contains(event.target)) this.dialog?.focus({ preventScroll: true });
    }

    // Long carts on short screens: when the anchored summary would leave too little room for the
    // items, the whole panel scrolls instead (nothing is clipped).
    updateStackMode() {
      const panel = this.panel;
      if (!panel) return;
      this.classList.remove('nxd--stack');
      const head = panel.querySelector('.nxd__head');
      const body = panel.querySelector('.nxd__body');
      const foot = panel.querySelector('.nxd__foot');
      if (!head || !body || !foot) return;
      const room = panel.clientHeight - head.offsetHeight - foot.offsetHeight;
      this.classList.toggle('nxd--stack', room < Math.min(body.scrollHeight, 160));
    }

    /* ---------- rendering ---------- */

    getSectionsToRender() {
      return [{ id: 'cart-drawer', selector: '#CartDrawer' }, { id: 'cart-icon-bubble' }];
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      return parseHTML(html).querySelector(selector).innerHTML;
    }

    getSectionDOM(html, selector = '.shopify-section') {
      return parseHTML(html).querySelector(selector);
    }

    // After /cart/add (product-form.js, quick add, nx-home.js): the response carries our sections.
    renderContents(parsedState) {
      this.productId = parsedState && parsedState.id;
      const applied = parsedState && parsedState.sections && this.applySections(parsedState.sections);
      if (!applied) this.refresh();
      this.open();
    }

    sectionIds() {
      return ['cart-drawer', 'cart-icon-bubble', ...this.cartPageSections().map((s) => s.section)];
    }

    // On /cart the page's own sections are refreshed together with the drawer.
    cartPageSections() {
      return ['main-cart-items', 'main-cart-footer']
        .map((id) => ({ id, el: document.getElementById(id) }))
        .filter((s) => s.el && s.el.dataset.id)
        .map((s) => ({ id: s.id, el: s.el, section: s.el.dataset.id }));
    }

    // The order note travels with the form to checkout; this also keeps it on the cart if the shopper leaves.
    saveNote(field) {
      clearTimeout(this.noteTimer);
      const url = `${window.routes?.cart_update_url || '/cart/update'}.js`;
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: field.value }),
      }).catch(() => {});
    }

    applySections(sections) {
      let rendered = false;
      const drawerHtml = sections['cart-drawer'];
      const panel = this.panel;
      if (drawerHtml && panel) {
        const fresh = parseHTML(drawerHtml).querySelector('.drawer__inner');
        if (fresh) {
          const note = panel.querySelector('[data-nxd-note]');
          const keepNote = note ? { value: note.value, open: note.closest('details').open } : null;
          panel.innerHTML = fresh.innerHTML;
          const nextNote = keepNote && panel.querySelector('[data-nxd-note]');
          if (nextNote) {
            nextNote.value = keepNote.value;
            if (keepNote.open) nextNote.closest('details').open = true;
          }
          ['data-nxd-sig', 'data-item-count'].forEach((attr) => panel.setAttribute(attr, fresh.getAttribute(attr) || ''));
          rendered = true;
        }
      }
      const bubbleHtml = sections['cart-icon-bubble'];
      const icon = document.getElementById('cart-icon-bubble');
      if (bubbleHtml && icon) {
        const source = parseHTML(bubbleHtml).querySelector('.shopify-section');
        if (source) icon.innerHTML = source.innerHTML;
      }
      const count = Number((this.panel && this.panel.dataset.itemCount) || 0);
      this.cartPageSections().forEach(({ el, section }) => {
        if (!sections[section]) return;
        const target = el.querySelector('.js-contents');
        const source = parseHTML(sections[section]).querySelector(`#${el.id} .js-contents`);
        if (target && source) target.innerHTML = source.innerHTML;
      });
      if (document.getElementById('main-cart-items')) {
        document.querySelector('cart-items')?.classList.toggle('is-empty', count === 0);
        document.getElementById('main-cart-footer')?.classList.toggle('is-empty', count === 0);
      }
      if (rendered) {
        this.classList.toggle('is-empty', count === 0);
        this.renderedAt = Date.now();
        if (this.classList.contains('active')) this.updateStackMode();
      }
      return rendered;
    }

    // Section Rendering API on the cart route (renders the drawer and, on /cart, the cart page sections).
    async fetchSections() {
      const response = await fetch(`${window.routes.cart_url}?sections=${this.sectionIds().join(',')}`);
      if (!response.ok) throw new Error(`sections ${response.status}`);
      const sections = await response.json();
      if (!sections || !sections['cart-drawer']) throw new Error('sections missing');
      return sections;
    }

    // Re-read the cart from Shopify (reopen, other tabs, other scripts). Only swaps the drawer when
    // the cart actually differs, and never while the shopper's own changes are being sent.
    async refresh() {
      if (this.busy || this.pending.size) return;
      const token = (this.refreshToken = (this.refreshToken || 0) + 1);
      let sections;
      try {
        sections = await this.fetchSections();
      } catch (error) {
        return;
      }
      if (token !== this.refreshToken || this.busy || this.pending.size || !sections['cart-drawer']) return;
      const fresh = parseHTML(sections['cart-drawer']).querySelector('.drawer__inner');
      if (fresh && this.panel && fresh.getAttribute('data-nxd-sig') === this.panel.getAttribute('data-nxd-sig')) {
        this.renderedAt = Date.now();
        return;
      }
      const focus = this.captureFocus();
      this.applySections(sections);
      this.restoreFocus(focus);
    }

    /* ---------- line controls ---------- */

    lineLimits(input) {
      return {
        min: Math.max(1, Number(input.dataset.min) || 1),
        step: Math.max(1, Number(input.step) || 1),
        max: input.max ? Number(input.max) : Infinity,
      };
    }

    syncStepper(line) {
      const input = line.querySelector('.nxd-qty__input');
      const { min, max } = this.lineLimits(input);
      const value = Number(input.value) || 0;
      line.querySelector('[data-nxd-step="1"]')?.setAttribute('aria-disabled', String(value >= max));
      if (value < min) line.querySelector('[data-nxd-step="-1"]')?.setAttribute('aria-disabled', 'true');
      else line.querySelector('[data-nxd-step="-1"]')?.removeAttribute('aria-disabled');
    }

    onStep(button) {
      if (button.getAttribute('aria-disabled') === 'true') return;
      const line = button.closest('.nxd-line');
      const input = line && line.querySelector('.nxd-qty__input');
      if (!input) return;
      const { min, step, max } = this.lineLimits(input);
      const current = Number(input.value) || 0;
      const direction = Number(button.dataset.nxdStep);
      // Like Dawn's stepper, "−" at the minimum removes the line.
      const next = direction < 0 && current <= min ? 0 : Math.min(Math.max(current + step * direction, min), max);
      if (next === current) return;
      input.value = next;
      this.syncStepper(line);
      this.queueLine(line, next);
    }

    onRemove(button) {
      const line = button.closest('.nxd-line');
      if (line) this.queueLine(line, 0);
    }

    onQuantityInput(input) {
      const line = input.closest('.nxd-line');
      if (!line) return;
      const confirmed = Number(input.dataset.confirmed);
      const wanted = input.dataset.requested ? Number(input.dataset.requested) : confirmed;
      const raw = String(input.value).trim();
      const quantity = Number(raw);
      if (raw === '' || !Number.isInteger(quantity) || quantity < 0) {
        input.value = wanted;
        return;
      }
      if (quantity === wanted) return;
      const { min, step, max } = this.lineLimits(input);
      let message = '';
      if (quantity > 0 && quantity < min) message = fill(strings().quantityMin, { quantity: min });
      else if (quantity > max) message = fill(strings().quantityMax, { quantity: max });
      else if (quantity % step !== 0) message = fill(strings().quantityStep, { quantity: step });
      if (message) {
        input.value = wanted;
        this.syncStepper(line);
        this.showLineError(line, message);
        this.say(message, true);
        return;
      }
      this.queueLine(line, quantity);
    }

    queueLine(line, quantity) {
      const input = line.querySelector('.nxd-qty__input');
      if (input) input.dataset.requested = String(quantity);
      this.showLineError(line, '');
      line.setAttribute('aria-busy', 'true');
      return this.requestQuantity(line.dataset.key, quantity, { title: line.dataset.title });
    }

    showLineError(line, message) {
      const box = line.querySelector('[data-nxd-line-error]');
      const input = line.querySelector('.nxd-qty__input');
      if (!box) return;
      box.textContent = message;
      box.hidden = !message;
      if (input) {
        if (message) input.setAttribute('aria-describedby', box.id);
        else input.removeAttribute('aria-describedby');
      }
    }

    /* ---------- cart changes: one request at a time, newest wish wins ---------- */

    requestQuantity(key, quantity, meta = {}) {
      if (!key) return Promise.resolve();
      this.errors.delete(key);
      this.pending.set(key, { quantity: Number(quantity), ...meta });
      this.setCheckoutBusy(true);
      if (!this.busy) {
        this.say(strings().updating || '');
        this.drain();
      }
      return this.whenIdle();
    }

    whenIdle() {
      if (!this.busy && !this.pending.size) return Promise.resolve(!this.lastHadErrors);
      return new Promise((resolve) => this.waiters.push(resolve));
    }

    async drain() {
      this.busy = true;
      let last = null;
      while (this.pending.size) {
        const [key, op] = this.pending.entries().next().value;
        this.pending.delete(key);
        last = await this.sendChange(key, op);
      }
      let sections = last && last.ok ? last.data.sections : null;
      if (!sections || this.needsResync) {
        try {
          sections = await this.fetchSections();
          this.needsResync = false;
        } catch (error) {
          sections = null;
          this.errors.set('_cart', strings().error);
        }
      }
      if (this.pending.size) {
        // More changes arrived while re-reading the cart: send them before showing anything.
        this.drain();
        return;
      }
      try {
        this.render(sections);
      } catch (error) {
        console.error(error);
        this.lastHadErrors = true;
      } finally {
        this.busy = false;
        const ok = !this.lastHadErrors;
        this.waiters.splice(0).forEach((resolve) => resolve(ok));
      }
    }

    async sendChange(key, op) {
      try {
        const response = await fetch(
          `${window.routes.cart_change_url}`,
          jsonPost({ id: key, quantity: op.quantity, sections: this.sectionIds(), sections_url: window.location.pathname })
        );
        const data = await response.json().catch(() => null);
        if (!response.ok || !data || data.status || data.errors) {
          const detail = data && (data.description || (typeof data.errors === 'string' ? data.errors : '') || data.message);
          this.errors.set(key, detail && detail !== 'Cart Error' ? detail : strings().error);
          this.needsResync = true;
          return { ok: false, data };
        }
        if (op.quantity > 0) {
          const line = (data.items || []).find((item) => item.key === key);
          if (line && line.quantity !== op.quantity) {
            this.errors.set(key, fill(strings().quantityMax, { quantity: line.quantity }));
          }
        } else if (op.title) {
          this.removedTitle = op.title;
        }
        this.lastState = data;
        this.needsResync = false;
        return { ok: true, data };
      } catch (error) {
        this.errors.set(key, strings().error);
        this.needsResync = true;
        return { ok: false };
      }
    }

    render(sections) {
      const panel = this.panel;
      const focus = this.captureFocus();
      const bodyScroll = this.body ? this.body.scrollTop : 0;
      const panelScroll = panel ? panel.scrollTop : 0;
      const rendered = !!sections && this.applySections(sections);
      if (!rendered) {
        // Shopify could not be reached: put the controls back to the last confirmed quantities.
        this.querySelectorAll('.nxd-line').forEach((line) => {
          const input = line.querySelector('.nxd-qty__input');
          if (input) {
            input.value = input.dataset.confirmed;
            delete input.dataset.requested;
            this.syncStepper(line);
          }
          line.removeAttribute('aria-busy');
        });
        this.setCheckoutBusy(false);
      }
      if (this.body) this.body.scrollTop = bodyScroll;
      if (this.panel) this.panel.scrollTop = panelScroll;

      const messages = [];
      this.errors.forEach((message, key) => {
        const line = key !== '_cart' && this.querySelector(`.nxd-line[data-key="${CSS.escape(key)}"]`);
        if (line) this.showLineError(line, message);
        else {
          const box = this.querySelector('[data-nxd-cart-error]');
          if (box) {
            box.textContent = message;
            box.hidden = false;
          }
        }
        if (!messages.includes(message)) messages.push(message);
      });
      this.errors.clear();
      this.lastHadErrors = messages.length > 0;
      this.restoreFocus(focus);
      this.announce(messages);

      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && this.lastState) {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: this.lastState });
      }
    }

    captureFocus() {
      const el = document.activeElement;
      const panel = this.panel;
      if (!el || !panel || !panel.contains(el)) return null;
      const line = el.closest('.nxd-line');
      const lines = [...panel.querySelectorAll('.nxd-line')];
      let role = null;
      if (el.matches('[data-nxd-step="-1"]')) role = '[data-nxd-step="-1"]';
      else if (el.matches('[data-nxd-step="1"]')) role = '[data-nxd-step="1"]';
      else if (el.matches('.nxd-qty__input')) role = '.nxd-qty__input';
      else if (el.matches('[data-nxd-remove]')) role = '[data-nxd-remove]';
      else if (el.id) role = `#${CSS.escape(el.id)}`;
      return { key: line && line.dataset.key, index: line ? lines.indexOf(line) : -1, role, panel: el === panel };
    }

    restoreFocus(info) {
      if (!info || !this.classList.contains('active')) return;
      const panel = this.panel;
      let target = null;
      if (info.key) {
        const line = panel.querySelector(`.nxd-line[data-key="${CSS.escape(info.key)}"]`);
        if (line) target = info.role ? line.querySelector(info.role) : null;
        else {
          // The line was removed: continue at the neighbouring item, or the empty-cart action.
          const lines = panel.querySelectorAll('.nxd-line');
          const near = lines[Math.min(info.index, lines.length - 1)];
          target = near ? near.querySelector('.nxd-line__title a') : panel.querySelector('.nxd-empty__cta');
        }
      } else if (info.role && !info.panel) {
        target = panel.querySelector(info.role);
      }
      (isShown(target) ? target : this.dialog).focus({ preventScroll: !isShown(target) });
    }

    say(message, assertive = false) {
      const region = this.querySelector(assertive ? '#CartDrawer-Alert' : '#CartDrawer-Status');
      if (!region) return;
      region.textContent = '';
      if (message) setTimeout(() => (region.textContent = message), 60);
    }

    announce(errorMessages) {
      const panel = this.panel;
      const count = Number(panel && panel.dataset.itemCount) || 0;
      let message = this.removedTitle ? `${fill(strings().removed, { title: this.removedTitle })} ` : '';
      this.removedTitle = null;
      if (count) {
        message += fill(strings().updated, {
          count: (panel.querySelector('#CartDrawer-Count')?.textContent || '').trim(),
          subtotal: (panel.querySelector('[data-nxd-subtotal]')?.textContent || '').trim(),
        });
      } else {
        message += strings().empty || '';
      }
      this.say(message.trim());
      if (errorMessages.length) this.say(errorMessages.join(' '), true);
    }

    setCheckoutBusy(on) {
      const button = this.querySelector('#CartDrawer-Checkout');
      if (!button) return;
      if (on) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }

    // Checkout always uses Shopify's confirmed cart: if changes are still being sent, wait for them,
    // and only continue when they all succeeded (otherwise the shopper sees the error first).
    onSubmit(event) {
      const form = event.target;
      if (!form || form.id !== 'CartDrawer-Form') return;
      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') {
        event.preventDefault();
        return;
      }
      if (this.busy || this.pending.size) {
        event.preventDefault();
        if (this.checkoutQueued) return;
        this.checkoutQueued = true;
        this.whenIdle().then((ok) => {
          this.checkoutQueued = false;
          const freshForm = this.querySelector('#CartDrawer-Form');
          const button = this.querySelector('#CartDrawer-Checkout');
          if (!ok || !freshForm || !button) return;
          if (typeof freshForm.requestSubmit === 'function') freshForm.requestSubmit(button);
          else {
            const flag = document.createElement('input');
            flag.type = 'hidden';
            flag.name = 'checkout';
            freshForm.appendChild(flag);
            freshForm.submit();
          }
        });
        return;
      }
      if (submitter.getAttribute('aria-disabled') === 'true') {
        event.preventDefault();
        return;
      }
      submitter.setAttribute('aria-disabled', 'true');
      setTimeout(() => submitter.removeAttribute('aria-disabled'), 8000);
    }
  }

  customElements.define('cart-drawer', CartDrawer);

  const CartItemsBase = typeof CartItems === 'function' ? CartItems : HTMLElement;

  class CartDrawerItems extends CartItemsBase {
    // Subscriptions and the update queue live on <cart-drawer>, which outlives each re-render.
    connectedCallback() {}

    disconnectedCallback() {}

    // Changes are queued by <cart-drawer> without the cart page's debounce.
    onChange() {}

    // Dawn's index-based API, kept for other scripts (e.g. <cart-remove-button>).
    updateQuantity(line, quantity) {
      const row = this.querySelector(`.nxd-line[data-line="${line}"]`);
      const drawer = this.closest('cart-drawer');
      if (row && drawer) return drawer.queueLine(row, Number(quantity));
    }

    getSectionsToRender() {
      return [
        { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
        { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
      ];
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}
